/**
 * Sentinel id for the collection-level cache tag in the RTK Query
 * "provide a list" recipe.
 *
 * It is an arbitrary string (the RTK docs note you could equally use `ALL` or
 * `*`), chosen so it never collides with a real entity id.
 *
 * @see https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 */
export const LIST_TAG_ID = 'LIST';

/** Cache tags returned by {@link provideListTags}. */
export type ListTags<TagType extends string> = { type: TagType; id: string | number }[];

/**
 * Build the `providesTags` value for a list query following the `id: 'LIST'`
 * recipe: a per-item tag `{ type, id }` for every entity, plus a single
 * collection-level `{ type, id: 'LIST' }` tag.
 *
 * A single-entity mutation invalidating `{ type, id }` refetches both the
 * affected entity query and any list providing that per-item tag; a
 * create/bulk mutation invalidating `{ type, id: 'LIST' }` refetches the
 * collection without touching unrelated entities.
 *
 * @param entities - the list result, or `undefined` when it has not loaded /
 *   errored. For wrapped/paginated responses, pass the extracted array
 *   (e.g. `result?.data.data`).
 * @param type - the cache tag type for this resource.
 *
 * @example
 * ```ts
 * providesTags: (result) => provideListTags(result, 'Users'),
 * // paginated:
 * providesTags: (result) => provideListTags(result?.data.data, 'Vaults'),
 * ```
 */
export function provideListTags<TagType extends string>(
  entities: readonly { id: string | number }[] | undefined,
  type: TagType,
): ListTags<TagType> {
  const listTag = { type, id: LIST_TAG_ID };
  return entities ? [...entities.map((entity) => ({ type, id: entity.id })), listTag] : [listTag];
}
